/**
 * Format dayjs object
 * @author Harish.R <harish.r@314ecorp.com>
 */
// TODO: dateParser validate
import _ from 'lodash';

import dateParser from 'lib/dateParser';
import useAppStore from 'store/app';
import {
	DEFAULT_TIME_ZONE,
	MINUTES_IN_HOUR,
	MONTH_DATE_YEAR_SLASH_FORMAT,
	MONTH_DATE_YEAR_TIME_FORMAT,
	YEAR_MONTH_DATE_FORMAT,
	YEAR_MONTH_DATE_TIME_FORMAT_WITH_TIMEZONE,
} from '@veritable/common/constants';

/**
 * custom date presets for Range Picker
 */
const DATE_PRESETS = {
	today: {
		label: 'Today',
		value: [dateParser().startOf('day'), dateParser().endOf('day')],
	},
	yesterday: {
		label: 'Yesterday',
		value: [dateParser().subtract(1, 'day').startOf('day'), dateParser().subtract(1, 'day').endOf('day')],
	},
	monthToDate: {
		label: 'This month',
		value: [dateParser().startOf('month'), dateParser().endOf('day')],
	},
	lastMonth: {
		label: 'Last month',
		value: [dateParser().subtract(1, 'month').startOf('month'), dateParser().subtract(1, 'month').endOf('month')],
	},
	lastThreeMonths: {
		label: 'Last 3 months',
		value: [dateParser().subtract(3, 'months'), dateParser().endOf('day')],
	},
	lastSixMonths: {
		label: 'Last 6 months',
		value: [dateParser().subtract(6, 'months'), dateParser().endOf('day')],
	},
	thisYear: {
		label: 'Year to date',
		value: [dateParser().startOf('year'), dateParser().endOf('day')],
	},
	lastYear: {
		label: 'Last year',
		value: [dateParser().subtract(1, 'year').startOf('year'), dateParser().subtract(1, 'year').endOf('year')],
	},
};

/**
 * custom date presets for Range Picker
 */
const customDatePresets: ICustomRange[] = _.values(DATE_PRESETS);

/**
 * function to return utc time in iso format
 * @returns {boolean} getIsAfter
 */
const getTimeInISOFormat = (time: IDate): string => {
	const parsed = dateParser.utc(time);
	if (parsed.isValid()) {
		return parsed.toISOString();
	}
	return '';
};

/**
 * function to change date format
 */
const changeDateFormat = (dateString: string, inputFormat: string, outputFormat = MONTH_DATE_YEAR_SLASH_FORMAT): string | undefined => {
	const date = dateParser(dateString, inputFormat);
	if (dateString && date.isValid()) {
		return date.format(outputFormat);
	}
};

/**
 * function to change array of dayjs object to date range of startDate and endDate strings
 */
const changeDayjsToDateRange = (date: IDate, outputFormat: string): EligibilityRequests.IDateRange => {
	const startDate = date ? date[0].format(outputFormat) : '';
	const endDate = date ? date[1].format(outputFormat) : '';
	return { startDate, endDate };
};

/**
 * function to return date string in practice zone
 */
const getDateStringInPracticeZone = (dateInUTC: string, format = MONTH_DATE_YEAR_TIME_FORMAT): string => {
	const practiceZone = useAppStore.getState().setupInfo?.timezone || DEFAULT_TIME_ZONE;
	const parsedDate = dateParser.utc(dateInUTC).tz(practiceZone);
	if (parsedDate.isValid()) {
		return parsedDate.format(format);
	}
	return '';
};

/**
 * function to return date string in practice zone
 */
const getTimeFromNowInPracticeZone = (dateInUTC: string): string => {
	const practiceZone = useAppStore.getState().setupInfo?.timezone || DEFAULT_TIME_ZONE;
	const parsedDate = dateParser.utc(dateInUTC).tz(practiceZone);
	if (parsedDate.isValid()) {
		return parsedDate.fromNow();
	}
	return '';
};

/**
 * convert a dateParser array to a list of string
 */
const covertDateToStringArray = (date: IDate[]): [string, string] => {
	return [date[0]?.format(YEAR_MONTH_DATE_FORMAT), date[1]?.format(YEAR_MONTH_DATE_FORMAT)];
};

/**
 * get label for utc offset & zone name
 */
const getOffsetString = (zone: string): string => {
	const utcOffset = dateParser().tz(zone).utcOffset();
	const isPositive = utcOffset > 0;
	const offsetHours = _.toInteger(utcOffset / MINUTES_IN_HOUR);
	const offsetMinutes = Math.abs(utcOffset % MINUTES_IN_HOUR);
	const hours = offsetHours?.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
	const minutes = offsetMinutes?.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
	return `${zone} ( ${isPositive ? '+' : ''} ${hours}:${minutes} )`;
};

/**
 * get all time zone options
 */
const TIME_ZONES = _.map(_.without(Intl.supportedValuesOf('timeZone'), 'Factory'), (value) => ({ label: getOffsetString(value), value }));

/**
 * validate timezone
 */
const isValidTimezone = (timezone: string): boolean => {
	return !!_.find(TIME_ZONES, { value: timezone });
};

/**
 * convert date range to timezone
 * This function is use to convert date range to timezone format to send to api. The dates that are created
 * in server like (inquiry date, creation time) etc,
 * if we have to inquiry on those dates, we need to pass proper date time with timezone so that BE can recalculate and convert it to UTC
 */
const convertDateRangeToTimeZone = (dateRange: string[]): EligibilityRequests.IDatetimeRange => {
	const practiceZone = useAppStore.getState().setupInfo?.timezone || DEFAULT_TIME_ZONE;
	const startDatetime = dateParser.tz(_.first(dateRange), practiceZone).startOf('day').format(YEAR_MONTH_DATE_TIME_FORMAT_WITH_TIMEZONE);
	const endDatetime = dateParser.tz(_.last(dateRange), practiceZone).endOf('day').format(YEAR_MONTH_DATE_TIME_FORMAT_WITH_TIMEZONE);
	return { startDatetime, endDatetime };
};

// function to get time difference between two dates
const getTimeDifference = (startDate: string, endDate: string): string => {
	const pluralize = (n: number, singular: string, plural: string): string => `${n} ${_.eq(n, 1) ? singular : plural}`;

	let start = dateParser.utc(startDate).clone();
	let end = dateParser.utc(endDate).clone();

	if (!(start.isValid() && end.isValid())) {
		return '';
	}
	if (end?.isBefore(start)) {
		[start, end] = [end, start];
	}

	const years = end.diff(start, 'year');
	start = start.add(years, 'year');

	const days = end.diff(start, 'day');
	start = start.add(days, 'day');

	const hours = end.diff(start, 'hour');
	start = start.add(hours, 'hour');

	const minutes = end.diff(start, 'minute');
	start = start.add(minutes, 'minute');

	const parts = _.compact([
		years ? pluralize(years, 'yr', 'yrs') : '',
		days ? pluralize(days, 'day', 'days') : '',
		hours ? pluralize(hours, 'hr', 'hrs') : '',
		minutes ? `${minutes} min` : '',
		!minutes && `${end.diff(start, 'second') || 1} sec`,
	]);

	return _.isEmpty(parts) ? null : parts.join(' ');
};

export {
	DATE_PRESETS,
	TIME_ZONES,
	customDatePresets,
	changeDateFormat,
	changeDayjsToDateRange,
	getTimeInISOFormat,
	getDateStringInPracticeZone,
	getTimeFromNowInPracticeZone,
	covertDateToStringArray,
	isValidTimezone,
	convertDateRangeToTimeZone,
	getTimeDifference,
};
